import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ExpressionStructure } from "abreact/expression/ui/editor/readonly/ExpressionStructure";
import { SummaryElementType } from "abreact/expression/ui/editor/readonly/ExpressionSummaryElement";
import { ExpressionSummaryModel, SummaryMode } from "abreact/expression/ui/editor/readonly/ExpressionSummaryModel";
import { DefaultOperandValueValueRenderer } from "abreact/expression/ui/editor/readonly/OperandValueValueRenderer";
import { AbImage } from "abreact/misc/AbImage";
import { StringUtils } from "abweb/utils/StringUtils";
import React from "react";
// import OperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperandValue;

export class GuidedExpressionSummaryReadOnlyGenerator {
  constructor(expressionDomainModel, valueRenderer) {
    _defineProperty(this, "valueRenderer", void 0);
    this.expressionDomainModel = expressionDomainModel;
    this.valueRenderer = valueRenderer ?? new DefaultOperandValueValueRenderer(expressionDomainModel);
  }
  generateRichSummaryForOperandValue(operandValueInfo, leafNodeOperatorID) {
    return this.generateRichSummaryForNode(operandValueInfo.toExpExpressionNode(), leafNodeOperatorID);
  }
  generateStringSummaryForOperandValue(operandValueInfo, indent, singleLine, leafNodeOperatorID, includeAdvanced) {
    return this.generateStringSummaryForNode(operandValueInfo.toExpExpressionNode(), undefined, singleLine, leafNodeOperatorID, includeAdvanced);
  }
  generateRichSummary(expExpressionDefinition) {
    return this.generateRichSummaryForModel(this.createSummaryModel(this.expressionDomainModel, expExpressionDefinition, false));
  }
  generateStringSummary(expExpressionDefinition, indent, singleLine) {
    return this.generateStringSummaryForModel(this.createSummaryModel(this.expressionDomainModel, expExpressionDefinition, true), indent, singleLine);
  }
  generateRichSummaryForNode(expExpressionNode, leafNodeOperatorID) {
    return this.generateRichSummaryForModel(this.createSummaryModelForNode(this.expressionDomainModel, expExpressionNode, false, leafNodeOperatorID));
  }
  generateStringSummaryForNode(expExpressionNode, indent, singleLine, leafNodeOperatorID, includeAdvanced) {
    return this.generateStringSummaryForModel(this.createSummaryModelForNode(this.expressionDomainModel, expExpressionNode, true, leafNodeOperatorID), indent, singleLine);
  }
  generateRichSummaryForModel(summaryModel) {
    try {
      if (summaryModel?.hasContent) {
        return /*#__PURE__*/React.createElement(React.Fragment, null, summaryModel.content.map((row, index) => {
          let isAdditionalCloseParen = false;
          let content = null;
          if (row.elements && row.elements.length > 0) {
            content = /*#__PURE__*/React.createElement("div", {
              key: row.operatorNode?.value?.id ?? index,
              className: `ab-gee-expression-summary-row ${this.computeReactRowClassName(row.depth)}`
            }, row.elements.map(element => {
              let computedClassName = this.computeReactElementClassName(element, isAdditionalCloseParen);
              if (element.type === SummaryElementType.CLOSE_PAREN) {
                isAdditionalCloseParen = true;
              }
              const valueToRender = element.expSummaryElementOptions?.noRichValue ? this.valueRenderer.stringValue(element) : this.valueRenderer.richValue(element);
              return /*#__PURE__*/React.createElement("span", {
                key: element.value,
                className: computedClassName
              }, valueToRender);
            }), summaryModel.summaryMode !== SummaryMode.ADVANCED_ONLY && this.generateAdvancedButtonForRow(row));
          }
          return content;
        }));
      } else {
        return null;
      }
    } catch (e) {
      return e.message;
    }
  }
  generateAdvancedButtonForRow(row) {
    let tooltip = null;
    if (row.operatorNode) {
      let advancedSummaryModel = new ExpressionSummaryModel();
      advancedSummaryModel.populateFromStructureNode(row.operatorNode, SummaryMode.ADVANCED_ONLY);
      tooltip = this.generateRichSummaryForModel(advancedSummaryModel);
      return tooltip ? /*#__PURE__*/React.createElement("span", {
        className: "ab-gee-expression-summary-row-element-advanced-icon"
      }, /*#__PURE__*/React.createElement(AbImage, {
        className: "ab-gee-expression-summary-advanced-options",
        image: "absdk:options",
        tooltip: tooltip
      })) : null;
    }
  }
  computeReactElementClassName(summaryElement, isAdditionalCloseParen) {
    let suffix;
    switch (summaryElement.type) {
      case SummaryElementType.AND:
      case SummaryElementType.OR:
        suffix = "connective";
        break;
      case SummaryElementType.CLOSE_PAREN:
        if (isAdditionalCloseParen) {
          suffix = "additional-close-paren";
        }
        break;
    }
    suffix = suffix ?? summaryElement.type;
    let computedClassName = "ab-gee-expression-summary-row-element-" + suffix.toLowerCase();
    if (summaryElement.expSummaryElementOptions?.cssClassName) {
      computedClassName = StringUtils.cssClassStr(computedClassName, summaryElement.expSummaryElementOptions.cssClassName);
    }
    return computedClassName;
  }
  computeReactRowClassName(depth) {
    return `ab-gee-expression-summary-row-indent-level-${depth}`;
  }
  generateStringSummaryForModel(summaryModel, indent, singleLine) {
    try {
      if (summaryModel) {
        let linebreak = "\n";
        if (singleLine) {
          linebreak = " ";
          indent = "";
        }
        return summaryModel.content.filter(row => row.elements && row.elements.length > 0).map(row => {
          let joinChars = " ";
          let prefixChars = "";
          return this.computeStringIndent(row.depth, indent ?? "   ") + row.elements.map(element => {
            if (element.type === SummaryElementType.CLOSE_PAREN) {
              joinChars = "";
              prefixChars = "";
            } else {
              prefixChars = joinChars === "" ? " " : "";
              joinChars = " ";
            }
            return prefixChars + this.valueRenderer.stringValue(element) + joinChars;
          }).filter(line => line.trim() !== "").join("").trim();
        }).join(linebreak);
      } else {
        return "";
      }
    } catch (e) {
      return e.message;
    }
  }
  computeStringIndent(depth, indent) {
    let indentContent = "";
    if (depth > 1) {
      indentContent = Array(depth).join(indent);
    }
    return indentContent;
  }
  createSummaryModel(expressionDomainModel, expExpressionDefinition, includeAdvanced) {
    let summaryModel;
    let structure = new ExpressionStructure(expressionDomainModel);
    structure.initFromExpressionDefinition(expExpressionDefinition);
    summaryModel = new ExpressionSummaryModel();
    summaryModel.populateFromStructure(structure, includeAdvanced ? SummaryMode.ALL_OPERANDS : SummaryMode.EXCLUDE_ADVANCED);
    return summaryModel;
  }
  createSummaryModelForNode(expressionDomainModel, expExpressionNode, includeAdvanced, leafNodeOperatorID) {
    const structure = new ExpressionStructure(expressionDomainModel);
    structure.initFromExpressionNode(expExpressionNode, leafNodeOperatorID);
    const summaryModel = new ExpressionSummaryModel();
    summaryModel.populateFromStructure(structure, includeAdvanced ? SummaryMode.ALL_OPERANDS : SummaryMode.EXCLUDE_ADVANCED);
    return summaryModel;
  }
}
//# sourceMappingURL=GuidedExpressionSummaryReadOnlyGenerator.js.map
